import React, { useState, useEffect, useMemo } from 'react';
import { useTimeClock } from '../hooks/useTimeClock';
import { ScreensaverSettings, WeatherUnits } from '../../shared/types.js';
import Button from './Button';
import { cloneDeep } from '../utils.js';

const Fieldset: React.FC<{ legend: string; children: React.ReactNode; className?: string }> = ({ legend, children, className }) => (
    <div className={`border border-gray-200 dark:border-gray-700 rounded-lg p-4 pt-2 ${className}`}>
        <legend className="px-2 font-semibold text-gray-700 dark:text-gray-200 text-lg">{legend}</legend>
        <div className="pt-2 space-y-3">{children}</div>
    </div>
);

const Checkbox: React.FC<{
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
}> = ({ label, checked, onChange, disabled }) => (
    <label className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} className="w-4 h-4 bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded text-brand-secondary focus:ring-brand-secondary"/>
        <span>{label}</span>
    </label>
);

const ScreensaverSettingsTab: React.FC = () => {
    const { screensaverSettings, updateScreensaverSettings } = useTimeClock();
    const [localSettings, setLocalSettings] = useState<ScreensaverSettings>(cloneDeep(screensaverSettings));
    const [citySearch, setCitySearch] = useState('');
    const [searchMessage, setSearchMessage] = useState('');

    useEffect(() => {
        setLocalSettings(cloneDeep(screensaverSettings));
    }, [screensaverSettings]);

    const handleUpdate = <K extends keyof ScreensaverSettings>(key: K, value: ScreensaverSettings[K]) => {
        setLocalSettings(prev => ({ ...prev, [key]: value }));
    };

    const handleWidgetUpdate = (widget: keyof ScreensaverSettings['widgets'], value: boolean) => {
        setLocalSettings(prev => ({
            ...prev,
            widgets: {
                ...prev.widgets,
                [widget]: value,
            }
        }));
    };

    const isDirty = useMemo(() => {
        return JSON.stringify(screensaverSettings) !== JSON.stringify(localSettings);
    }, [screensaverSettings, localSettings]);

    const handleApply = () => {
        updateScreensaverSettings(localSettings);
    };
    
    const handleRevert = () => {
        setLocalSettings(cloneDeep(screensaverSettings));
    };

    const handleGeocodeSearch = async () => {
        if (!citySearch.trim()) {
            setSearchMessage('Please enter a city name.');
            return;
        }
        setSearchMessage('Searching...');
        try {
            const response = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(citySearch)}&count=1`);
            if (!response.ok) {
                throw new Error('Geocoding API request failed.');
            }
            const data = await response.json();
            if (data.results && data.results.length > 0) {
                const { latitude, longitude, name, admin1, country } = data.results[0];
                setLocalSettings(prev => ({
                    ...prev,
                    latitude: parseFloat(latitude.toFixed(2)),
                    longitude: parseFloat(longitude.toFixed(2)),
                }));
                const locationName = [name, admin1, country].filter(Boolean).join(', ');
                setSearchMessage(`Found: ${locationName}`);
            } else {
                setSearchMessage(`City "${citySearch}" not found.`);
            }
        } catch (error) {
            console.error('Geocoding error:', error);
            setSearchMessage('An error occurred during search.');
        }
    };

    const inputClasses = "w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-1 px-2 focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary text-gray-900 dark:text-white disabled:opacity-50";
    const labelClass = "block text-sm font-medium text-gray-600 dark:text-gray-300";

    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-2xl mx-auto text-gray-900 dark:text-white">
            <div className="space-y-8">
                <Fieldset legend="General">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <span className="font-medium">Enable Screensaver</span>
                        <div className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${localSettings.enabled ? 'bg-brand-secondary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                            <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${localSettings.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                            <input type="checkbox" className="absolute w-full h-full opacity-0 cursor-pointer" checked={localSettings.enabled} onChange={e => handleUpdate('enabled', e.target.checked)} />
                        </div>
                    </label>

                    <div className="grid grid-cols-2 items-center gap-4">
                        <label htmlFor="timeout-minutes" className="font-medium">Timeout</label>
                        <div className="flex items-center gap-2">
                             <input
                                id="timeout-minutes"
                                type="number"
                                value={localSettings.timeoutMinutes}
                                onChange={(e) => handleUpdate('timeoutMinutes', parseInt(e.target.value, 10) || 1)}
                                min="1"
                                className={`${inputClasses} w-24 text-center`}
                                disabled={!localSettings.enabled}
                            />
                            <span>minutes</span>
                        </div>
                    </div>
                </Fieldset>
                <Fieldset legend="Widgets">
                    <div className="space-y-2 pt-2">
                        <Checkbox label="Time" checked={localSettings.widgets.time} onChange={(v) => handleWidgetUpdate('time', v)} disabled={!localSettings.enabled} />
                        <Checkbox label="Date" checked={localSettings.widgets.date} onChange={(v) => handleWidgetUpdate('date', v)} disabled={!localSettings.enabled} />
                        <Checkbox
                            label="Weather"
                            checked={localSettings.widgets.weather}
                            onChange={(v) => handleWidgetUpdate('weather', v)}
                            disabled={!localSettings.enabled}
                        />
                    </div>
					<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Choose what to display on the screensaver.</p>
                </Fieldset>

                {localSettings.enabled && localSettings.widgets.weather && (
                    <Fieldset legend="Weather Options">
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="city-search" className={labelClass}>City Search</label>
                                <div className="mt-1 flex gap-2">
                                    <input
                                        type="text"
                                        id="city-search"
                                        value={citySearch}
                                        onChange={(e) => setCitySearch(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleGeocodeSearch(); }}
                                        className={inputClasses}
                                        placeholder="e.g., New York"
                                    />
                                    <Button type="button" variant="secondary" onClick={handleGeocodeSearch}>Find</Button>
                                </div>
                                {searchMessage && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{searchMessage}</p>}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="latitude" className={labelClass}>Latitude</label>
                                    <input type="number" step="any" id="latitude" value={localSettings.latitude || 0} onChange={(e) => handleUpdate('latitude', parseFloat(e.target.value) || 0)} className={inputClasses} />
                                </div>
                                <div>
                                    <label htmlFor="longitude" className={labelClass}>Longitude</label>
                                    <input type="number" step="any" id="longitude" value={localSettings.longitude || 0} onChange={(e) => handleUpdate('longitude', parseFloat(e.target.value) || 0)} className={inputClasses} />
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Temperature Unit</label>
                                <div className="mt-2 flex gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" name="weatherUnits" value={WeatherUnits.IMPERIAL} checked={localSettings.weatherUnits === WeatherUnits.IMPERIAL} onChange={() => handleUpdate('weatherUnits', WeatherUnits.IMPERIAL)} className="text-brand-secondary focus:ring-brand-secondary"/>
                                        <span>Fahrenheit</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" name="weatherUnits" value={WeatherUnits.METRIC} checked={localSettings.weatherUnits === WeatherUnits.METRIC} onChange={() => handleUpdate('weatherUnits', WeatherUnits.METRIC)} className="text-brand-secondary focus:ring-brand-secondary"/>
                                        <span>Celsius</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </Fieldset>
                )}
            </div>
            <div className="flex justify-end gap-4 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                <Button variant="secondary" onClick={handleRevert} disabled={!isDirty}>Revert</Button>
                <Button onClick={handleApply} disabled={!isDirty}>Apply</Button>
            </div>
        </div>
    );
};

export default ScreensaverSettingsTab;
